 
import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  Action,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { AccessControllerService, PermissionDataModel } from '@osos/shared/api';
import {
  AccessRightLevelEnum,
  AccessRightModel,
  AppContext,
  DEFAULT_TABLE_SIZE,
  DataEntryPresenterModel,
  ENTITLED_ENTITY_TYPE,
  EntitledEntityTypeEnum,
  EntitlementMode,
  LoadingIndicator,
  ManagedHttpContext,
  Page,
  PermissionsPresenterModel,
  RefProperty,
  Where,
} from '@osos/shared/types';
import { Observable } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { PermissionsFactoryService } from '../../factories/permissions-factory/permissions-factory.service';
import { ApiUtilsService } from '../../services/api-utils/api-utils.service';
import { AppState } from '../app/app.state';
import { LoadingActions } from '../loading/loading.actions';
import { UserPreferencesActions } from '../user-preferences/user-preferences.actions';
import { PermissionActions } from './permissions.actions';

export interface PermissionsStateModel {
  userPage: Page<PermissionsPresenterModel>;
  groupsPage: Page<PermissionsPresenterModel>;
  presentedUsers: PermissionsPresenterModel[];
  presentedGroups: PermissionsPresenterModel[];
  loading: LoadingIndicator;
  transitoryUsers: DataEntryPresenterModel[];
  transitoryGroups: DataEntryPresenterModel[];
  currentUserPermissions: CurrentUserPermissions;
  activeRouteAccessLevel: AccessRightLevelEnum;
  adminAccessLevel: AccessRightLevelEnum;
}

export type CurrentUserPermissions = { [key: string]: AccessRightLevelEnum };

@State<PermissionsStateModel>({
  name: 'permissions',
  defaults: {
    userPage: {
      items: [],
      totalCount: 0,
    },
    groupsPage: {
      items: [],
      totalCount: 0,
    },
    presentedUsers: [],
    presentedGroups: [],
    loading: new LoadingIndicator(),
    transitoryGroups: [],
    transitoryUsers: [],
    currentUserPermissions: {},
    activeRouteAccessLevel: null,
    adminAccessLevel: undefined,
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class PermissionsState {
  constructor(
    private permissionsFactoryService: PermissionsFactoryService,
    private accessControllerService: AccessControllerService,
    private apiHelper: ApiUtilsService,
    private router: Router,
  ) {}

  ngxsAfterBootstrap(ctx?: StateContext<any>): void {
    // Set the current user permissions
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        ctx.setState(
          patch<PermissionsStateModel>({
            activeRouteAccessLevel: ctx.getState().adminAccessLevel,
          }),
        );
      });
  }

  // TODO Change to create selector after add tags and role
  @Selector([PermissionsState])
  static totalUsersCount(state: PermissionsStateModel): number {
    return state.userPage.totalCount;
  }
  @Selector([PermissionsState])
  static totalGroupsCount(state: PermissionsStateModel): number {
    return state.groupsPage.totalCount;
  }
  @Selector([PermissionsState])
  static presentedUsers(
    state: PermissionsStateModel,
  ): PermissionsPresenterModel[] {
    return state.presentedUsers;
  }

  @Selector([PermissionsState])
  static presentedGroups(
    state: PermissionsStateModel,
  ): PermissionsPresenterModel[] {
    return state.presentedGroups;
  }

  @Selector([PermissionsState])
  static getTransitoryUsers(
    state: PermissionsStateModel,
  ): DataEntryPresenterModel[] {
    return state.transitoryUsers;
  }
  @Selector([PermissionsState])
  static getTransitoryGroups(
    state: PermissionsStateModel,
  ): DataEntryPresenterModel[] {
    return state.transitoryGroups;
  }
  /**
   * @description This map will contains all the general and specific permission with their top level
   * for example
   * {
   *  system: 1,
   *  DataModule:439fe32b-4af0-4e01-b344-1fca663db877: 1
   *  DataEntity:439fe32b-4af0-4e01-b344-1fca663db877: 2
   * }
   * @param state PermissionsStateModel
   * @returns CurrentUserPermissions
   */
  @Selector([PermissionsState])
  static getCurrentUserPermissions(
    state: PermissionsStateModel,
  ): CurrentUserPermissions {
    return state.currentUserPermissions;
  }

  @Selector([PermissionsState])
  static getUserAuthenticatedViewsCount(state: PermissionsStateModel): number {
    return Object.keys(state.currentUserPermissions).filter(
      (key) => key.indexOf('DataView') > -1,
    ).length;
  }

  @Selector([PermissionsState, AppState.currentAppContext])
  static getActiveRouteAccessLevel(
    state: PermissionsStateModel,
    appContext: AppContext,
  ): AccessRightLevelEnum {
    return appContext === AppContext.Public
      ? AccessRightLevelEnum.AdminAccess
      : state.activeRouteAccessLevel;
  }

  @Selector([PermissionsState])
  static getCurrentAdminLevel(
    state: PermissionsStateModel,
  ): AccessRightLevelEnum {
    return state.adminAccessLevel;
  }

  @Action(PermissionActions.FetchPermissions)
  public fetchPermissions(
    { setState, dispatch }: StateContext<PermissionsStateModel>,
    { payload }: PermissionActions.FetchPermissions,
  ): Observable<any> {
    dispatch(new LoadingActions.SetLoading(true));
    if (payload.entitledEntityType === EntitledEntityTypeEnum.Group) {
      setState(
        patch<PermissionsStateModel>({
          loading: patch<LoadingIndicator>({
            page: true,
          }),
          presentedGroups: [],
        }),
      );
    } else {
      setState(
        patch<PermissionsStateModel>({
          loading: patch<LoadingIndicator>({
            page: true,
          }),
          presentedUsers: [],
        }),
      );
    }

    let where: Where<PermissionDataModel>;
    if (payload.entitledEntityType === EntitledEntityTypeEnum.Group) {
      where = {
        or: this.getGroupSearchCriteriaCondition(payload.searchTerm),
      };
    } else {
      where = {
        or: this.getUserSearchCriteriaCondition(payload.searchTerm),
      };
    }

    const filter = this.apiHelper.prepareFilter<PermissionDataModel>({
      where,
      skip: payload.skip ?? 0,
      limit: payload.limit ?? DEFAULT_TABLE_SIZE,
      order: this.prepareOrder(payload),
    });
    return this.accessControllerService
      .getPermissions({ ...payload, ...filter })
      .pipe(
        switchMap((elements) =>
          this.accessControllerService
            .getPermissionsCount({
              ...payload,
              where: this.apiHelper.prepareWhere<PermissionDataModel>(where),
            })
            .pipe(
              tap((page) => {
                if (elements) {
                  const mappedResult = elements.map((item) =>
                    this.permissionsFactoryService.createPresenterModel(item),
                  );
                  if (
                    payload.entitledEntityType === EntitledEntityTypeEnum.Group
                  ) {
                    setState(
                      patch<PermissionsStateModel>({
                        presentedGroups: mappedResult,
                        groupsPage: {
                          items: elements as any,
                          totalCount: page[0].count,
                        } as Page<PermissionsPresenterModel>,
                      }),
                    );
                  } else {
                    setState(
                      patch<PermissionsStateModel>({
                        presentedUsers: mappedResult,
                        userPage: {
                          items: elements as any,
                          totalCount: page[0].count,
                        } as Page<PermissionsPresenterModel>,
                      }),
                    );
                  }
                }
              }),
            ),
        ),
        finalize(() => {
          dispatch(new LoadingActions.SetLoading(false));
          setState(
            patch<PermissionsStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(PermissionActions.ChangeUserPermission)
  changeUserPermission(
    { setState, dispatch }: StateContext<PermissionsStateModel>,
    { payload }: PermissionActions.ChangeUserPermission,
  ): Observable<any> {
    let convenientLevel = payload.level;
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<PermissionsStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    // incase edit permission for module or entity (downgrade from edit to view
    // => we will send none access to backend , but still exist it as view in frontend)
    if (
      payload.level === AccessRightLevelEnum.ViewAccess &&
      payload.entitlementType != EntitlementMode.System
    ) {
      convenientLevel = AccessRightLevelEnum.ViewAccess;
      payload.level = AccessRightLevelEnum.None;
    }
    return this.accessControllerService
      .updateAccessPermission({
        ...payload,
        body: {
          level: payload.level,
        },
      })
      .pipe(
        tap(() => {
          setState(
            patch<PermissionsStateModel>({
              presentedUsers: updateItem(
                (u) => u.id == payload.entitledEntityId,
                patch({
                  permission: patch<any>({
                    value: patch({
                      level: convenientLevel,
                    }),
                  }),
                }),
              ),
            }),
          );
        }),

        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<PermissionsStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(PermissionActions.ChangeGroupPermission)
  changeGroupPermission(
    { setState, dispatch }: StateContext<PermissionsStateModel>,
    { payload }: PermissionActions.ChangeGroupPermission,
  ): Observable<any> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    let convenientLevel = payload.level;
    setState(
      patch<PermissionsStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    if (
      payload.level == AccessRightLevelEnum.ViewAccess &&
      payload.entitlementType != EntitlementMode.System
    ) {
      convenientLevel = AccessRightLevelEnum.ViewAccess;
      payload.level = AccessRightLevelEnum.None;
    }
    return this.accessControllerService
      .updateAccessPermission({
        ...payload,
        body: {
          level: payload.level,
        },
      })
      .pipe(
        tap(() => {
          setState(
            patch<PermissionsStateModel>({
              presentedGroups: updateItem(
                (g) => g.id == payload.entitledEntityId,
                patch({
                  permission: patch<any>({
                    value: patch({
                      level: convenientLevel,
                    }),
                  }),
                }),
              ),
            }),
          );
        }),

        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<PermissionsStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(PermissionActions.FetchModuleUsersWithViewAccess)
  FetchModuleUsersWithViewAccess(
    { setState }: StateContext<PermissionsStateModel>,
    payload: PermissionActions.FetchModuleUsersWithViewAccess,
  ): Observable<any> {
    //TODO remove language codes from hared coded in search
    const where = {
      or: this.getUserSearchCriteriaCondition(payload.filter.searchTerm),
    } as Where<PermissionDataModel>;
    const filter = this.apiHelper.prepareFilter<PermissionDataModel>({
      skip: 0,
      limit: DEFAULT_TABLE_SIZE,
      where,
    });

    return this.accessControllerService
      .getSystemPermissions({
        entitledEntityType: ENTITLED_ENTITY_TYPE.User,
        moduleId: payload.filter?.moduleId ?? 'null',
        filter: filter,
      })
      .pipe(
        tap((res) => {
          setState(
            patch<PermissionsStateModel>({
              transitoryUsers: res.map(
                (item) =>
                  this.permissionsFactoryService.createPresenterModel(
                    item,
                  ) as DataEntryPresenterModel,
              ),
            }),
          );
        }),
      );
  }
  @Action(PermissionActions.FetchModuleGroupsWithViewAccess)
  FetchModuleGroupsWithViewAccess(
    { setState }: StateContext<PermissionsStateModel>,
    payload: PermissionActions.FetchModuleGroupsWithViewAccess,
  ): Observable<any> {
    //TODO remove language codes from hared coded in search
    const where = {
      or: this.getGroupSearchCriteriaCondition(payload.filter.searchTerm),
    } as Where<PermissionDataModel>;
    const filter = this.apiHelper.prepareFilter<PermissionDataModel>({
      skip: 0,
      limit: DEFAULT_TABLE_SIZE,
      where: where,
    });
    return this.accessControllerService
      .getSystemPermissions({
        entitledEntityType: ENTITLED_ENTITY_TYPE.Group,
        moduleId: payload.filter?.moduleId ?? 'null',
        filter: filter,
      })
      .pipe(
        tap((res) => {
          setState(
            patch<PermissionsStateModel>({
              transitoryGroups: res.map(
                (item) =>
                  this.permissionsFactoryService.createPresenterModel(
                    item,
                  ) as DataEntryPresenterModel,
              ),
            }),
          );
        }),
      );
  }
  @Action(PermissionActions.FetchUserPermissions)
  FetchUserPermissions({
    setState,
    dispatch,
  }: StateContext<PermissionsStateModel>): Observable<any> {
    return this.accessControllerService
      .getUserPermissions({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap((res: PermissionDataModel[]) => {
          if (res?.length && res[0]?.permission?.length) {
            // Prepare the current user permissions, to be patched once to the state:
            let adminLevel = AccessRightLevelEnum.None;
            const _userPermissions = res[0].permission
              .map((perm) => perm as AccessRightModel)
              .reduce((acc: CurrentUserPermissions, p: AccessRightModel) => {
                //This should be system permission
                //check if there is already existed level for this permission
                //TODO later check with the backend if we will receive duplicate permissions for the pType
                const pType = !p.value.entitlement
                  ? this.getSystemPermissionKey()
                  : this.generateReferencePropertyUniqueKey(
                      p.value.entitlement,
                    );

                const oldLevel = acc[pType];
                if (!p.value.entitlement) {
                  adminLevel =
                    oldLevel && oldLevel > p.value.level
                      ? oldLevel
                      : p.value.level;
                }

                return {
                  ...acc,
                  [pType]:
                    oldLevel && oldLevel > p.value.level
                      ? oldLevel
                      : p.value.level,
                };
              }, {} as CurrentUserPermissions);
            //When the user has no permission, set none to system access
            if (
              !Object.keys(_userPermissions).includes(
                this.getSystemPermissionKey(),
              )
            ) {
              _userPermissions[this.getSystemPermissionKey()] =
                AccessRightLevelEnum.None;
            }
            setState(
              patch<PermissionsStateModel>({
                currentUserPermissions: _userPermissions,
                adminAccessLevel: adminLevel,
              }),
            );
            if (adminLevel === AccessRightLevelEnum.None) {
              dispatch(new UserPreferencesActions.DisableTours());
            }
          }
        }),
      );
  }

  @Action(PermissionActions.SetActiveRouteAccessLevel)
  SetActiveRouteAccessLevel(
    { setState }: StateContext<PermissionsStateModel>,
    { payload }: PermissionActions.SetActiveRouteAccessLevel,
  ): void {
    setState(
      patch<PermissionsStateModel>({
        activeRouteAccessLevel: payload,
      }),
    );
  }

  private generateReferencePropertyUniqueKey(r: RefProperty) {
    if (r == null) return this.getSystemPermissionKey();
    return `${r.dataEntity}:${r.dataEntry}`;
  }

  private getSystemPermissionKey(): string {
    return EntitlementMode.System.toString();
  }

  getUserSearchCriteriaCondition(search = '') {
    return this.apiHelper.prepareSearch(search, [
      'value.account_information.username',
      ...this.apiHelper.prepareFilterBy([
        'value.basic_info.first_name',
        'value.basic_info.full_name',
        'value.basic_info.last_name',
      ]),
      'value.account_information.email_address',
    ]);
  }

  getGroupSearchCriteriaCondition(search = '') {
    return this.apiHelper.prepareLocalizedSearch(search, ['value.name']);
  }

  private prepareOrder(payload): string[] {
    return payload.sort && payload.sort?.sortBy
      ? [`${payload.sort?.sortBy} ${payload.sort?.sortDir?.toUpperCase()}`]
      : undefined;
  }
}
